import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { useKeenSlider } from "keen-slider/react";
import { CompareSpecs } from "@widgets/CompareSpecs";
import { ClearCompare } from "@features/compare/ClearCompare";
import { MergeCompare } from "@features/compare/merge-compare";
import { ProductCardCompare } from "@entities/compare/ui/ProductCardCompare";
import { StickyCardCompare } from "@entities/compare/ui/StickyCardCompare";
import { LeftIcon } from "@shared/ui/Icon/ui/Common/LeftIcon";
import { RightIcon } from "@shared/ui/Icon/ui/Common/RightIcon";
import { ToggleSwitch } from "@shared/ui/ToggleSwitch";
import { DEFAULT_SCROLL_TOP, visibleSlidesQnt } from "./constants";
import { CompareContentProps } from "./types";
import classes from "./compare-content.module.scss";

export const CompareContent = ({
    visibleOffers,
    localOffers,
    compareId,
    shouldHideActions,
    isAuthenticated
}: CompareContentProps) => {
    const [showDifferencesOnly, setShowDifferencesOnly] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentLocalSlide, setCurrentLocalSlide] = useState(0);
    const [isSticky, setIsSticky] = useState(false);

    const totalOffersMain = visibleOffers.length;
    const totalOffersLocal = localOffers.length;
    const lastSlide = totalOffersMain - visibleSlidesQnt.MAX;
    const showLeftArrow = currentSlide > 0;
    const showRightArrow = currentSlide < lastSlide;
    const visibleSpecsOffers = visibleOffers.slice(
        currentSlide,
        currentSlide + visibleSlidesQnt.MAX
    );

    const lastLocalSlide = totalOffersLocal - visibleSlidesQnt.MAX;
    const showLocalLeftArrow = currentLocalSlide > 0;
    const showLocalRightArrow = currentLocalSlide < lastLocalSlide;

    const hasLocalOffers = localOffers.length > 0;
    const dynamicScrollTop = hasLocalOffers ? DEFAULT_SCROLL_TOP * 2 : DEFAULT_SCROLL_TOP;

    const [sliderRef, sliderInstance] = useKeenSlider<HTMLDivElement>({
        slides: { perView: visibleSlidesQnt.MAX, spacing: 16 },
        breakpoints: {
            "(max-width: 1200px)": { slides: { perView: visibleSlidesQnt.MID, spacing: 0 } },
            "(max-width: 768px)": { slides: { perView: visibleSlidesQnt.MIN, spacing: 0 } }
        },
        loop: false,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        }
    });

    const [localSliderRef, localSliderInstance] = useKeenSlider<HTMLDivElement>({
        slides: { perView: visibleSlidesQnt.MAX, spacing: 16 },
        breakpoints: {
            "(max-width: 1200px)": { slides: { perView: visibleSlidesQnt.MID, spacing: 0 } },
            "(max-width: 768px)": { slides: { perView: visibleSlidesQnt.MIN, spacing: 0 } }
        },
        loop: false,
        slideChanged(slider) {
            setCurrentLocalSlide(slider.track.details.rel);
        }
    });

    const toggleDifferences = () => {
        setShowDifferencesOnly(!showDifferencesOnly);
    };

    const handleScroll = () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        setIsSticky(scrollTop > dynamicScrollTop);
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [dynamicScrollTop]);

    useEffect(() => {
        if (sliderInstance.current) {
            sliderInstance.current.update();
        }
    }, [visibleOffers]);

    useEffect(() => {
        if (localSliderInstance.current) {
            localSliderInstance.current.update();
        }
    }, [localOffers]);

    return (
        <>
            {hasLocalOffers && (
                <div className={classes.compare__local}>
                    <div className={classes.compare__top}>
                        <span className={classes.compare__quantity}>
                            {totalOffersLocal} объявлений в очереди для сравнения
                        </span>
                        <div className={classes["compare__local-actions"]}>
                            {!shouldHideActions && <MergeCompare />}
                            <ClearCompare isLocalOnly />
                        </div>
                    </div>
                    <div className={classes["compare__cards-container"]}>
                        {showLocalLeftArrow && (
                            <button
                                className={classNames(
                                    classes.compare__arrow,
                                    classes["compare__arrow--left"]
                                )}
                                onClick={() => localSliderInstance.current?.prev()}
                            >
                                <LeftIcon className={classes["compare__arrow--color"]} />
                            </button>
                        )}
                        <div
                            ref={localSliderRef}
                            className={classNames("keen-slider", classes["compare__local-cards"])}
                        >
                            {localOffers.map((offer) => (
                                <div className="keen-slider__slide" key={offer.id}>
                                    <ProductCardCompare
                                        offer={offer}
                                        hideDeleteMode={!shouldHideActions}
                                    />
                                </div>
                            ))}
                        </div>
                        {showLocalRightArrow && (
                            <button
                                className={classNames(
                                    classes.compare__arrow,
                                    classes["compare__arrow--right"]
                                )}
                                onClick={() => localSliderInstance.current?.next()}
                            >
                                <RightIcon className={classes["compare__arrow--color"]} />
                            </button>
                        )}
                    </div>
                </div>
            )}
            <div className={classes.compare__top}>
                <span className={classes.compare__quantity}>{totalOffersMain} объявлений</span>
                {!shouldHideActions && <ClearCompare isLocalOnly={!isAuthenticated} />}
            </div>
            <div
                className={classNames(classes["compare__cards-container"], {
                    [classes["compare__cards-container--sticky"]]: isSticky
                })}
            >
                {showLeftArrow && (
                    <button
                        className={classNames(
                            classes.compare__arrow,
                            classes["compare__arrow--left"]
                        )}
                        onClick={() => sliderInstance.current?.prev()}
                    >
                        <LeftIcon className={classes["compare__arrow--color"]} />
                    </button>
                )}
                <div
                    ref={sliderRef}
                    className={classNames("keen-slider", classes.compare__cards, {
                        [classes["compare__cards--sticky"]]: isSticky
                    })}
                >
                    {visibleOffers.map((offer) => (
                        <div className="keen-slider__slide" key={offer.id}>
                            {isSticky ? (
                                <StickyCardCompare
                                    offer={offer}
                                    hideDeleteMode={!shouldHideActions}
                                />
                            ) : (
                                <ProductCardCompare
                                    offer={offer}
                                    hideDeleteMode={!shouldHideActions}
                                />
                            )}
                        </div>
                    ))}
                </div>
                {showRightArrow && (
                    <button
                        className={classNames(
                            classes.compare__arrow,
                            classes["compare__arrow--right"]
                        )}
                        onClick={() => sliderInstance.current?.next()}
                    >
                        <RightIcon className={classes["compare__arrow--color"]} />
                    </button>
                )}
            </div>
            <div
                className={classNames(classes.compare__placeholder, {
                    [classes["compare__placeholder--sticky"]]: isSticky
                })}
            />
            <div className={classes.compare__toolbar}>
                <h2 className={classes.compare__subtitle}>Характеристики</h2>
                <ToggleSwitch
                    label="Показать только различия"
                    onChange={toggleDifferences}
                    checked={showDifferencesOnly}
                />
            </div>
            <CompareSpecs
                visibleOffers={visibleSpecsOffers}
                showOnlyDifferences={showDifferencesOnly}
                compareId={compareId}
            />
        </>
    );
};